import { useEffect, useState } from "react";
import {
  useGetMe,
  useUpdateMePreferences,
  getGetMeQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Mail } from "lucide-react";

/**
 * Per-user Gmail preferences.
 *
 * Currently exposes a single toggle: "Mark Gmail messages as read on
 * view". When on, opening a Gmail message in MailCraft also clears the
 * `UNREAD` label on the Gmail side via the existing `gmail.modify`
 * scope. Default off so existing users who installed MailCraft as a
 * read-only inbox aren't surprised by their Gmail unread counts
 * suddenly changing.
 *
 * The explicit Mark read / Mark unread toggle on the detail page works
 * regardless of this preference.
 */
export function GmailPreferencesCard() {
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const updateMutation = useUpdateMePreferences();

  // Local mirror so the switch reacts instantly while the PATCH is in
  // flight, and so we can roll back on failure.
  const [markOnView, setMarkOnView] = useState<boolean>(false);

  useEffect(() => {
    if (me) {
      setMarkOnView(me.gmailMarkReadOnView ?? false);
    }
  }, [me]);

  const onToggle = async (next: boolean) => {
    const prev = markOnView;
    setMarkOnView(next);
    try {
      await updateMutation.mutateAsync({
        data: { gmailMarkReadOnView: next },
      });
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast.success(
        next
          ? "Gmail messages will be marked read when you open them."
          : "Opening a Gmail message no longer changes its read state.",
      );
    } catch (e) {
      setMarkOnView(prev);
      toast.error(apiErrorMessage(e, "Failed to save Gmail preference"));
    }
  };

  return (
    <Card data-testid="card-gmail-preferences">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="w-5 h-5" /> Gmail preferences
        </CardTitle>
        <CardDescription>
          Controls how MailCraft interacts with your connected Gmail
          account on your behalf.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label
              htmlFor="gmail-mark-read-on-view"
              className="text-sm font-medium cursor-pointer"
            >
              Mark Gmail messages as read when I open them
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              When on, opening a Gmail message in MailCraft also clears
              the <span className="font-mono">UNREAD</span> label in
              Gmail itself. Off by default so we never change your Gmail
              unread counts without you asking. The Mark read / Mark
              unread button on the message page always works regardless
              of this setting.
            </p>
          </div>
          <Switch
            id="gmail-mark-read-on-view"
            checked={markOnView}
            onCheckedChange={onToggle}
            disabled={updateMutation.isPending}
            data-testid="switch-gmail-mark-read-on-view"
          />
        </div>
      </CardContent>
    </Card>
  );
}
